import {
    Button,
    Dialog,
    Flexer,
    Grid,
    Heading,
    Input,
    InputControl,
    InputPrefix,
    Modal,
    ModalClose,
    Portal,
    Stack,
    View,
    useDialog,
    useInput,
    useToast,
    useVisibility,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ColorButton, Error, Loading, ProjectCard } from '../components'
import { COLORS } from '../constants'
import { byName, generateRandomPosition, logger, urlContainsId } from '../helpers/util'
import { useProjectUsers, useProjects } from '../hooks/api.hooks'
import { useAuth } from '../hooks/auth.hook'
import { useAppStore } from '../store/app'
import NewProjectModal from './new-project.modal'

export default function ProjectsModal({ teamName, teamId, onDismiss }) {
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(null)
    const [filter, setFilter] = useState('')
    const { workspaceId, projectId } = useParams()
    const navigate = useNavigate()
    const { getUserId } = useAuth()
    const actions = useAppStore((state) => state.actions)
    const projectUsersAPI = useProjectUsers()
    const projectsAPI = useProjects()
    const [projects, setProjects] = useState([])
    const [count, setCount] = useState(0)
    const newProjectModal = useVisibility(false)
    const [deleteProjectId, setDeleteProjectId] = useState(null)
    const { setDialog, closeDialog } = useDialog()
    const [deleteProjectUserId, setDeleteProjectUserId] = useState(null)
    const name = useInput('')
    const [color, setColor] = useState('')
    const { showToast } = useToast()
    const workspace = useAppStore((state) => state.workspace)
    //const isAdmin = useMemo(() => workspace.role == 'Owner' || workspace.role == 'Admin', [workspace.role])
    const isAdmin = useMemo(() => workspace.role != 'Guest', [workspace.role])

    const handleDelete = async () => {
        setLoading(true)
        setError(null)

        try {
            setDeleteProjectId(null)
            setDeleteProjectUserId(null)
            setLoading(false)
            fetchData()

            actions.projectDelete(deleteProjectId)
            actions.starredDelete(deleteProjectId)

            await projectUsersAPI.remove(workspaceId, deleteProjectId, deleteProjectUserId)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleLeave = async (project) => {
        const { projectUserId } = project
        const projectId = project.id

        setDialog({
            title: 'Are you sure?',
            description: 'You will lose access to this project and all the resources.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            actions.projectDelete(projectId)
                            actions.starredDelete(projectId)
                            closeDialog()
                            projectUsersAPI.remove(workspaceId, projectId, projectUserId).then(() => fetchData())
                            urlContainsId(projectId, () => navigate(`/app/workspace/${workspaceId}/today`))
                        }}
                        variant="danger">
                        Leave
                    </Button>
                </View>
            ),
        })
    }

    const handleJoin = async (project) => {
        setError(null)

        try {
            const projectId = project.id
            const users = [{ id: getUserId() }]
            // const newProject = await projectsAPI.getOne(workspaceId, projectId)
            // this will get sent from the API via websockets
            // actions.projectAdd(newProject, false)
            await projectUsersAPI.create(workspaceId, projectId, { users })
            fetchData()
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const projects = await projectsAPI.getAll(workspaceId)

            setProjects(projects.sort(byName))
            setCount(projects.length)
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleCreate = async () => {
        setLoading(true)
        setError(null)

        try {
            const project = await projectsAPI.create(workspaceId, {
                name: name.value,
                color,
                isClosed: true,
                isPrivate: false,
                isChannel: false,
                position: generateRandomPosition(),
                users: [{ id: getUserId() }],
            })

            actions.projectAdd(project)
            setLoading(false)
            fetchData()
            setColor('')
            name.setValue('')
            showToast({ message: 'New project created!', anchor: 'bottom-right' })
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    useEffect(() => {
        if (workspaceId) fetchData()
    }, [workspaceId])

    return (
        <>
            {newProjectModal.visible && (
                <NewProjectModal
                    noPrivateOption
                    onDismiss={newProjectModal.hide}
                    onCreate={fetchData}
                />
            )}

            <Dialog
                portal={Portal}
                closeButton
                isVisible={!!deleteProjectUserId}
                title="Leave this project?"
                description="You will no longer have access to this project & its' resources."
                onDismiss={() => setDeleteProjectUserId(null)}
                footer={
                    <>
                        <Button onClick={() => setDeleteProjectUserId(null)}>Cancel</Button>
                        <Flexer />
                        <Button
                            onClick={handleDelete}
                            variant="danger"
                            outline>
                            Leave
                        </Button>
                    </>
                }
            />

            <Modal
                width={900}
                height="90%"
                style={{ maxHeight: 900 }}
                isVisible={true}
                onDismiss={onDismiss}
                portal={Portal}
                header={
                    <>
                        <Heading as="h4">{teamName} Projects</Heading>
                        <Flexer />
                        <ModalClose onClick={onDismiss} />
                    </>
                }>
                <Loading loading={loading} />
                <Error error={error} />

                <Stack
                    direction="vertical"
                    spacing="1rem"
                    width="100%"
                    height="100%">
                    <View
                        row
                        gap={10}>
                        <InputControl>
                            <InputPrefix>
                                <ColorButton
                                    color={color}
                                    onChange={setColor}
                                    colors={COLORS}
                                />
                            </InputPrefix>
                            <Input
                                autoFocus
                                selectOnFocus
                                placeholder="New Website"
                                value={name.value}
                                onChange={name.onChange}
                            />
                        </InputControl>

                        <Button
                            onClick={handleCreate}
                            variant="accent"
                            disabled={!name.value || !isAdmin}>
                            Create Project
                        </Button>
                    </View>

                    <View
                        flex={1}
                        width="100%"
                        position="relative">
                        <View
                            height="100%"
                            width="100%"
                            position="absolute"
                            className="f-overflow-y-auto">
                            <Grid
                                columns={4}
                                gap="0.5rem">
                                {projects
                                    .filter((project: any) => project.name.toLowerCase().includes(filter.toLowerCase()))
                                    .map((project: any, index: number) => (
                                        <ProjectCard
                                            key={index}
                                            project={project}
                                            onJoin={handleJoin}
                                            onLeave={handleLeave}
                                            onNavigate={() => {
                                                navigate(`/app/workspace/${workspaceId}/c/${project.id}/list`)
                                                onDismiss()
                                            }}
                                        />
                                    ))}
                            </Grid>
                        </View>
                    </View>
                </Stack>
            </Modal>
        </>
    )
}
